import { Box, styled } from '@mui/material';
import { useNavigate } from 'react-router';

import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';

import useBrainSession from '../../../../services/BrainSession/useBrainSession.ts';

import ChevronIcon from './_icons/ChevronIcon.tsx';
import CreditScoreLarge from './_images/creditscore_large.png';
import CreditScoreSmall from './_images/creditscore_small.png';
import GjeldLarge from './_images/gjeld_large.png';
import GjeldSmall from './_images/gjeld_small.png';
import PropertyLarge from './_images/property_large.png';
import PropertySmall from './_images/property_small.png';
import RateLarge from './_images/rate_large.png';
import RateSmall from './_images/rate_small.png';

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#EFF7FF',
  borderRadius: 12,
  display: 'flex',
  width: '100%',
  maxWidth: '100%',
  height: 100,
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row',
  margin: '8px 0',
  [theme.breakpoints.up('sm')]: {
    maxWidth: 280,
    height: 270,
    flexDirection: 'column',
    paddingLeft: 32,
    paddingRight: 32,
    margin: '8px',
  },
  [theme.breakpoints.up('lg')]: {
    margin: '0 8px',
  },
}));

const StyledImage = styled('img')(({ theme }) => ({
  display: 'flex',
  alignSelf: 'center',
  height: 85,
  width: 80,
  [theme.breakpoints.up('sm')]: {
    height: 134,
    width: 220,
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  justifyContent: 'center',
  alignItems: 'flex-start',
  display: 'flex',
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  paddingLeft: 18,
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: 0,
  },
}));

const StyledLink = styled('p')(() => ({
  color: '#18A0FB',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  '&:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  '& > svg': {
    fill: 'none',
    fontSize: '1rem',
  },
}));

const boxes = [
  {
    title: 'Kredittsjekk deg selv',
    image: {
      small: CreditScoreSmall,
      large: CreditScoreLarge,
    },
    cta: {
      event: 'home_2ndsect_creditcheck_clicked' as const,
      bob: null as null | { flow: string; step: string },
      link: '/kredittsjekke-seg-selv',
    },
  },
  {
    title: 'Sjekk din gjeld',
    image: {
      small: GjeldSmall,
      large: GjeldLarge,
    },
    cta: {
      event: 'home_2ndsect_debt_clicked' as const,
      bob: null as null | { flow: string; step: string },
      link: '/mingjeld',
    },
  },
  {
    title: 'Sammenlign din rente',
    image: {
      small: RateSmall,
      large: RateLarge,
    },
    cta: {
      event: 'home_2ndsect_compare_clicked' as const,
      bob: {
        flow: 'debt_check',
        step: 'skip_start',
      },
      link: null,
    },
  },
  {
    title: 'Sjekk din boligverdi',
    image: {
      small: PropertySmall,
      large: PropertyLarge,
    },
    cta: {
      event: 'home_2ndsect_propertyvalue_clicked' as const,
      bob: null as null | { flow: string; step: string },
      link: '/boligverdi',
    },
  },
];

const StaticSaveWithScoopr: React.FC = () => {
  const { toggleChat } = useBrainSession();
  const navigate = useNavigate();

  return (
    <div>
      <h2 className="mb-5 flex w-full justify-center text-center typography-v1-h2 sm:mb-10">
        Ta kontroll over din økonomi
      </h2>
      <div className="flex w-full flex-col items-center justify-center sm:flex-row sm:flex-wrap">
        {boxes.map((b) => (
          <div key={b.title} className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4">
            <StyledBox>
              <StyledImage
                src={b.image.small}
                alt="description"
                loading="lazy"
                className="sm:hidden!"
              />
              <StyledImage
                src={b.image.large}
                alt="description"
                loading="lazy"
                className="hidden! sm:block!"
              />
              <ContentBox>
                <h4 className="mb-4 flex typography-v1-h4">{b.title}</h4>
                <StyledLink
                  className="typography-v1-button2"
                  onClick={() => {
                    if (b.cta.event) {
                      sendUserPortalEvent(b.cta.event);
                    }
                    if (b.cta.bob) {
                      toggleChat({
                        goal: b.cta.bob.flow,
                        step: b.cta.bob.step,
                      });
                    }
                    if (b.cta.link) {
                      void navigate(b.cta.link);
                    }
                  }}
                >
                  Sjekk nå <ChevronIcon />
                </StyledLink>
              </ContentBox>
            </StyledBox>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaticSaveWithScoopr;
